import React, { useMemo, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Hash, CalendarPlus, SkipForward, Calendar, GripVertical, AlertTriangle } from 'lucide-react';
import { ReportMetadata } from '../types';
import { inspectionRowsTotals } from '../services/inspectionFigures';
import { getServiceLog } from '../services/serviceJournal';
import { getCalendarWeek, getStartOfWeek, addDays, getEffectiveDueDate } from '../services/dateService';
import { istSifaProjekt } from '../services/projectType';
import { ProjectLock } from '../services/projectLockLogic';
import type { ListProject } from '../services/listProject';
import { ProjectLockHint } from './ProjectLockHint';

/**
 * Eine Karte der Tafel: ein Listeneintrag, bei Ueberfaelligem als Kopie mit
 * Kennung `_overdue`. Bleibt ein AUSZUG und wird hier nie gespeichert:
 * Ziehen und die Knoepfe rufen nur onPostpone/onSkip, und der Speicherweg
 * dahinter laedt das Projekt per Kennung neu (App.handlePostpone ->
 * folgeterminService).
 */
type KanbanEntry = ListProject & { _overdue?: true };

interface KanbanBoardProps {
    projects: ListProject[];
    grouping: 'weekday' | 'interval';
    viewMode: 'standard' | 'focus';
    weekOffset: number;
    onWeekChange: (offset: number) => void;
    onOpenProject: (project: ListProject) => void;
    onPostpone: (project: ListProject, days?: number) => void;
    onSkip: (project: ListProject) => void;
    /** Sperren der sichtbaren Projekte (aus useProjectLocks). */
    locks?: Map<string, ProjectLock>;
    currentUserId?: string | null;
    lockNowMs?: number;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
    projects, grouping, viewMode, weekOffset, onWeekChange, onOpenProject, onPostpone, onSkip,
    locks, currentUserId, lockNowMs
}) => {
    const [dragOverCol, setDragOverCol] = useState<string | null>(null);
    const [draggingId, setDraggingId] = useState<string | null>(null);

    const now = new Date();
    const displayDate = addDays(now, weekOffset * 7);
    const currentWeekStart = getStartOfWeek(displayDate);
    const currentKW = getCalendarWeek(displayDate);
    const endOfDisplayWeek = addDays(currentWeekStart, 6);
    endOfDisplayWeek.setHours(23, 59, 59, 999);
    const startStr = currentWeekStart.toISOString().split('T')[0];
    const endStr = endOfDisplayWeek.toISOString().split('T')[0];

    // Column definitions
    const columns = useMemo(() => {
        if (grouping === 'weekday') {
            const days = [
                { id: '1', label: 'Montag', offset: 0 },
                { id: '2', label: 'Dienstag', offset: 1 },
                { id: '3', label: 'Mittwoch', offset: 2 },
                { id: '4', label: 'Donnerstag', offset: 3 },
                { id: '5', label: 'Freitag', offset: 4 },
                { id: '6', label: 'Samstag', offset: 5 },
                { id: '0', label: 'Sonntag', offset: 6 },
            ];
            const cols = days.map(d => {
                const date = addDays(currentWeekStart, d.offset);
                const dateStr = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                const isoStr = date.toISOString().split('T')[0];
                const isToday = isoStr === now.toISOString().split('T')[0];
                return { id: d.id, label: d.label, dateLabel: dateStr, isoDate: isoStr, isToday, color: isToday ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-700' };
            });
            cols.push({ id: 'undefined', label: 'Nicht zugeordnet', dateLabel: '', isoDate: '', isToday: false, color: 'bg-slate-50 border-slate-200 text-slate-500' });
            return cols;
        }
        const intervals = [
            { id: 'daily', label: 'Täglich', color: 'bg-red-50 border-red-200 text-red-700' },
            { id: 'weekly', label: 'Wöchentlich', color: 'bg-blue-50 border-blue-200 text-blue-700' },
            { id: 'biweekly', label: '14-tägig', color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
            { id: 'twice_monthly', label: '2x Monat', color: 'bg-cyan-50 border-cyan-200 text-cyan-700' },
            { id: 'monthly', label: 'Monatlich', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
            { id: 'custom', label: 'Benutzerdefiniert', color: 'bg-purple-50 border-purple-200 text-purple-700' },
            { id: 'undefined', label: 'Nicht definiert', color: 'bg-slate-50 border-slate-200 text-slate-500' },
        ];
        return intervals.map(i => ({ ...i, dateLabel: '', isoDate: '', isToday: false }));
    }, [grouping, currentWeekStart, now]);

    // Group projects into columns
    const kanbanGroups = useMemo(() => {
        const groups: Record<string, KanbanEntry[]> = {};
        columns.forEach(c => { groups[c.id] = []; });

        projects.forEach(p => {
            const meta = p.data.metadata as ReportMetadata;
            const interval = meta.intervalConfig;
            // SiFa: der rohe DB-Termin stammt oft aus dem Altbestand (D1) und
            // wuerde die Betreuung als ueberfaellig in eine Wochentagsspalte
            // stellen. getEffectiveDueDate kennt die Ausnahme.
            const nextDateStr = istSifaProjekt(meta)
                ? getEffectiveDueDate(p)
                : (p.next_scheduled_date || interval?.nextScheduledDate);

            if (grouping === 'weekday') {
                let assigned = false;
                if (nextDateStr) {
                    if (nextDateStr >= startStr && nextDateStr <= endStr) {
                        const d = new Date(nextDateStr).getDay();
                        if (groups[String(d)]) { groups[String(d)].push(p); assigned = true; }
                    } else if (nextDateStr < startStr) {
                        // Overdue: place in preferred days or undefined
                        const pref = interval?.preferredWeekdays;
                        if (pref?.length) { pref.forEach((d: number) => { if (groups[String(d)]) { groups[String(d)].push({ ...p, _overdue: true }); assigned = true; } }); }
                        if (!assigned) { groups['undefined'].push({ ...p, _overdue: true }); assigned = true; }
                    }
                }
                if (!assigned) {
                    const pref = interval?.preferredWeekdays;
                    if (pref?.length) { pref.forEach((d: number) => { if (groups[String(d)]) { groups[String(d)].push(p); assigned = true; } }); }
                    if (!assigned) groups['undefined'].push(p);
                }
            } else {
                const type = interval?.type || 'undefined';
                if (groups[type]) groups[type].push(p);
                else groups['undefined'].push(p);
            }
        });
        return groups;
    }, [projects, grouping, columns, startStr, endStr]);

    // Drag & Drop handlers
    const handleDragStart = useCallback((e: React.DragEvent, projectId: string) => {
        e.dataTransfer.setData('text/plain', projectId);
        e.dataTransfer.effectAllowed = 'move';
        setDraggingId(projectId);
    }, []);

    const handleDragEnd = useCallback(() => { setDraggingId(null); setDragOverCol(null); }, []);

    const isDraggable = grouping === 'weekday';

    return (
        <div className="flex flex-col h-full">
            {/* Week navigation */}
            {grouping === 'weekday' && (
                <div className="flex justify-center items-center gap-4 mb-4 bg-white rounded-xl border border-slate-200 px-4 py-2.5">
                    <button onClick={() => onWeekChange(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><ChevronLeft className="w-4 h-4" /></button>
                    <div className="text-center">
                        <div className="font-bold text-slate-700 text-sm">KW {currentKW}</div>
                        <div className="text-[11px] text-slate-400">{currentWeekStart.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – {endOfDisplayWeek.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                    </div>
                    <button onClick={() => onWeekChange(1)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><ChevronRight className="w-4 h-4" /></button>
                    {weekOffset !== 0 && <button onClick={() => onWeekChange(0)} className="text-[11px] font-bold text-blue-600 hover:text-blue-800 px-2 py-0.5 bg-blue-50 rounded-full border border-blue-200">Heute</button>}
                </div>
            )}

            {/* Columns — position-based DnD on the container */}
            <div
                className="flex gap-3 overflow-x-auto pb-4 h-full items-start"
                onDragOver={isDraggable ? (e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    // Find which column the cursor is over by checking each column's bounding rect
                    const container = e.currentTarget as HTMLElement;
                    const children = Array.from(container.children) as HTMLElement[];
                    const x = e.clientX;
                    let foundCol: string | null = null;
                    children.forEach((child) => {
                        const rect = child.getBoundingClientRect();
                        if (x >= rect.left && x <= rect.right) {
                            foundCol = child.getAttribute('data-col-id');
                        }
                    });
                    if (foundCol && dragOverCol !== foundCol) setDragOverCol(foundCol);
                    else if (!foundCol && dragOverCol) setDragOverCol(null);
                } : undefined}
                onDragLeave={isDraggable ? (e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
                        setDragOverCol(null);
                    }
                } : undefined}
                onDrop={isDraggable ? (e) => {
                    e.preventDefault();
                    // Find target column from cursor position
                    const container = e.currentTarget as HTMLElement;
                    const children = Array.from(container.children) as HTMLElement[];
                    const x = e.clientX;
                    let targetColId: string | null = null;
                    children.forEach((child) => {
                        const rect = child.getBoundingClientRect();
                        if (x >= rect.left && x <= rect.right) {
                            targetColId = child.getAttribute('data-col-id');
                        }
                    });

                    setDragOverCol(null);
                    setDraggingId(null);

                    if (!targetColId || grouping !== 'weekday') return;
                    const projectId = e.dataTransfer.getData('text/plain');
                    if (!projectId) return;
                    const project = projects.find(p => p.id === projectId);
                    if (!project) return;

                    const col = columns.find(c => c.id === targetColId);
                    if (!col?.isoDate) return;

                    const targetDate = new Date(col.isoDate);
                    const currentDue = getEffectiveDueDate(project);
                    const currentDate = currentDue ? new Date(currentDue) : new Date();
                    currentDate.setHours(0, 0, 0, 0);
                    targetDate.setHours(0, 0, 0, 0);
                    const diffDays = Math.round((targetDate.getTime() - currentDate.getTime()) / 864e5);
                    if (diffDays !== 0) onPostpone(project, diffDays);
                } : undefined}
            >
                {columns.map(col => {
                    let items = kanbanGroups[col.id] || [];
                    if (viewMode === 'focus') items = items.filter(p => { const nd = p.next_scheduled_date || p.data.metadata?.intervalConfig?.nextScheduledDate; return nd && nd >= startStr && nd <= endStr; });
                    if (items.length === 0 && col.id === 'undefined') return null;
                    if (items.length === 0 && grouping === 'interval') return null;

                    const isDropTarget = dragOverCol === col.id;

                    return (
                        <div
                            key={col.id}
                            data-col-id={col.id}
                            className={`min-w-[260px] w-[260px] flex flex-col h-full rounded-xl border shrink-0 transition-all duration-150 ${isDropTarget ? 'border-blue-400 bg-blue-50/50 ring-2 ring-blue-200 scale-[1.01]' : 'border-slate-200 bg-slate-50/50'}`}
                        >
                            {/* Column header */}
                            <div className={`px-3 py-2.5 rounded-t-xl border-b text-xs font-bold uppercase tracking-wider flex justify-between items-center ${col.isToday ? 'bg-blue-100 border-blue-200 text-blue-700' : col.color}`}>
                                <div>
                                    <div>{col.label}</div>
                                    {col.dateLabel && <div className="text-[11px] font-normal opacity-70 normal-case">{col.dateLabel}</div>}
                                </div>
                                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${items.length > 0 ? 'bg-white/80 text-slate-600' : 'bg-white/40 text-slate-400'}`}>{items.length}</span>
                            </div>

                            {/* Cards */}
                            <div className="p-2 flex-1 overflow-y-auto space-y-2">
                                {items.length === 0 && (
                                    <div className={`h-20 border-2 border-dashed rounded-lg flex items-center justify-center text-[11px] text-slate-300 italic ${isDropTarget ? 'border-blue-300 bg-blue-50 text-blue-400' : 'border-slate-200'}`}>
                                        {isDropTarget ? 'Hier ablegen' : 'Keine Projekte'}
                                    </div>
                                )}
                                {items.map(p => {
                                    const meta = p.data.metadata as ReportMetadata;
                                    const interval = meta.intervalConfig;
                                    const status = p.status || meta.globalData?.status || 'planning';
                                    const isPlanning = status === 'planning';
                                    const isOverdue = p._overdue;
                                    // SiFa ohne Turnus: kein Termin, kein Verschieben — ein
                                    // Drop oder "+7d" schriebe sonst ein intervalConfig auf
                                    // die Betreuung (genau der D1-Fehler).
                                    const isSifa = istSifaProjekt(meta);
                                    const dueDateStr = isSifa
                                        ? getEffectiveDueDate(p)
                                        : (p.next_scheduled_date || interval?.nextScheduledDate);
                                    const dbNr = p.current_inspection_number || 0;
                                    const isDragging = draggingId === p.id;

                                    // Inspection progress — ohne Monatspauschale, mit den Nachweisen
                                    // auf der Pauschale (Pauschalprojekt: die Begehungsposition waechst
                                    // dort nicht mehr). Protokoll aus der Listenzeile, nichts nachgeladen.
                                    const { used: inspUs, contracted: inspCt } = inspectionRowsTotals(p.project_services, getServiceLog(p.data));
                                    const inspPct = inspCt > 0 ? Math.min(100, Math.round(inspUs / inspCt * 100)) : -1;

                                    const canAct = !isPlanning && !isSifa;
                                    const canDrag = isDraggable && !isPlanning && !isSifa;

                                    return (
                                        <div
                                            key={p.id}
                                            draggable={canDrag}
                                            onDragStart={isDraggable ? (e) => handleDragStart(e, p.id) : undefined}
                                            onDragEnd={isDraggable ? handleDragEnd : undefined}
                                            className={`bg-white rounded-lg border shadow-sm hover:shadow-md transition-all group relative ${isOverdue ? 'border-red-200 bg-red-50/30' : 'border-slate-200'} ${isDragging ? 'opacity-40 scale-95 rotate-1' : ''} ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                        >
                                            {/* Drag handle */}
                                            {canDrag && (
                                                <div className="absolute left-0 top-0 bottom-0 w-5 flex items-center justify-center opacity-0 group-hover:opacity-40 hover:!opacity-100 text-slate-400 transition-opacity">
                                                    <GripVertical className="w-3 h-3" />
                                                </div>
                                            )}

                                            <div onClick={() => onOpenProject(p)} className={`cursor-pointer p-3 ${isDraggable ? 'pl-5' : ''}`}>
                                                {/* Overdue badge */}
                                                {isOverdue && (
                                                    <div className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded mb-1.5 w-fit border border-red-200">
                                                        <AlertTriangle className="w-2.5 h-2.5" /> Überfällig
                                                    </div>
                                                )}

                                                <div className="font-bold text-slate-800 text-xs leading-snug group-hover:text-blue-600 line-clamp-2 transition-colors">{p.name}</div>
                                                <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                                                    <Hash className="w-2.5 h-2.5" /> {meta.projectNumber || '-'} · #{dbNr}
                                                </div>
                                                <ProjectLockHint lock={locks?.get(p.id)} currentUserId={currentUserId} nowMs={lockNowMs || 0} className="mt-1" />

                                                {/* Progress bar */}
                                                {inspPct >= 0 && (
                                                    <div className="mt-2">
                                                        <div className="flex justify-between text-[10px] mb-0.5">
                                                            <span className="text-slate-400">Begehungen</span>
                                                            <span className="font-bold text-slate-600">{inspUs}/{inspCt}</span>
                                                        </div>
                                                        <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                                                            <div className={`h-full rounded-full ${inspPct >= 90 ? 'bg-emerald-500' : inspPct >= 50 ? 'bg-blue-500' : 'bg-amber-400'}`} style={{ width: `${inspPct}%` }} />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Due date */}
                                                {!isPlanning && dueDateStr && (
                                                    <div className="text-[11px] mt-1.5 flex justify-between text-slate-400">
                                                        <span>{interval?.type || '—'}</span>
                                                        <span className={isOverdue || (dueDateStr < now.toISOString().split('T')[0]) ? 'text-red-500 font-bold' : 'font-medium'}>
                                                            {new Date(dueDateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Quick actions */}
                                            {canAct && (
                                                <div className="flex gap-1 px-2 py-1.5 border-t border-slate-100 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
                                                    <button onClick={e => { e.stopPropagation(); onPostpone(p, 7); }} className="flex-1 flex items-center justify-center gap-0.5 py-1 rounded text-[10px] font-bold text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition-colors" title="+1 Woche">
                                                        <CalendarPlus className="w-3 h-3" /> +7d
                                                    </button>
                                                    <button onClick={e => { e.stopPropagation(); onPostpone(p, -7); }} className="flex-1 flex items-center justify-center gap-0.5 py-1 rounded text-[10px] font-bold text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-colors" title="-1 Woche">
                                                        <Calendar className="w-3 h-3" /> -7d
                                                    </button>
                                                    <button onClick={e => { e.stopPropagation(); onSkip(p); }} className="flex-1 flex items-center justify-center gap-0.5 py-1 rounded text-[10px] font-bold text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Überspringen">
                                                        <SkipForward className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};